import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, CalendarOff, CalendarPlus } from "lucide-react";
import { useT, useLanguage } from "@/lib/LanguageContext";
import { errMsg, localeFor } from "./calendarUtils";
import { OverrideDialog } from "./OverrideDialog";
import { ExtraHoursDialog } from "./ExtraHoursDialog";

const fmtDate = (iso, loc) => new Intl.DateTimeFormat(loc, { day: "numeric", month: "short", year: "numeric" }).format(new Date(`${iso}T00:00:00`));

// The date-specific edits of the weekly hours, both read from the parent's
// GET /admin/availability-exceptions?date_from=today:
//   * BLOCKED DATES (kind "closed", date_from..date_to) — the practice is shut those days;
//   * EXTRA HOURS (kind "open", one date + start/end + services) — opening hours outside
//     the weekly grid, whose start times are computed exactly like a weekly window's. They
//     replaced the one-off slot the calendar used to create.
// A closure wins over extra hours on the same date (the server's rule, open_hours.py).
// Both kinds are deleted through the same DELETE /admin/availability-exceptions/{id};
// deleting extra hours never touches an appointment already booked inside them.
//
// `services` is the whole catalogue: the list names each row's services from it (a
// since-deactivated one still has a name), the dialog offers the active ones.
export const OverridesPanel = ({ exceptions, onChanged, services = [] }) => {
  const t = useT("admin");
  const { lang } = useLanguage();
  const loc = localeFor(lang);
  const [closureDialogOpen, setClosureDialogOpen] = useState(false);
  const [extraDialogOpen, setExtraDialogOpen] = useState(false);
  const closed = (exceptions || []).filter((e) => e.kind === "closed");
  const extra = (exceptions || [])
    .filter((e) => e.kind === "open")
    .sort((a, b) => `${a.date}T${a.start}`.localeCompare(`${b.date}T${b.start}`));
  const nameOf = (id) => services.find((s) => s.id === id)?.name;

  const remove = async (item, confirmKey, doneKey) => {
    if (!window.confirm(t(confirmKey))) return;
    try {
      await api.delete(`/admin/availability-exceptions/${item.id}`);
      toast.success(t(doneKey));
      onChanged && onChanged();
    } catch (e) {
      toast.error(errMsg(e, t));
    }
  };

  return (
    <div className="bg-eh-cream border border-eh-border-soft rounded-3xl p-6 space-y-8" data-testid="overrides-panel">
      <section>
        <div className="flex items-center justify-between gap-3 mb-1">
          <h2 className="font-display text-xl text-eh-ink">{t("ovr.title")}</h2>
          <button onClick={() => setClosureDialogOpen(true)} className="btn-primary text-sm inline-flex items-center gap-1.5" data-testid="ovr-add-btn">
            <Plus size={14} /> {t("ovr.add")}
          </button>
        </div>
        <p className="text-eh-muted text-sm mb-4">{t("ovr.subtitle")}</p>

        {closed.length === 0 && (
          <div className="text-eh-muted text-sm bg-white/50 border border-eh-border-soft rounded-2xl p-6 text-center" data-testid="ovr-empty">
            {t("ovr.empty")}
          </div>
        )}

        <div className="space-y-2">
          {closed.map((item) => (
            <div key={item.id} className="flex items-center gap-3 bg-white/60 border border-eh-border-soft rounded-xl px-4 py-2.5" data-testid={`ovr-item-${item.id}`}>
              <CalendarOff size={16} className="text-eh-muted shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-eh-ink">
                  {fmtDate(item.date_from, loc)}
                  {item.date_to && item.date_to !== item.date_from ? ` – ${fmtDate(item.date_to, loc)}` : ""}
                </div>
                {item.reason && <div className="text-xs text-eh-muted truncate">{item.reason}</div>}
              </div>
              <button onClick={() => remove(item, "ovr.confirmDelete", "ovr.deleted")} className="p-2 rounded-full hover:bg-red-50 text-red-600 shrink-0" data-testid={`ovr-delete-${item.id}`}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      </section>

      <section data-testid="ovr-extra-section">
        <div className="flex items-center justify-between gap-3 mb-1">
          <h2 className="font-display text-xl text-eh-ink">{t("ovr.extraTitle")}</h2>
          <button onClick={() => setExtraDialogOpen(true)} className="btn-primary text-sm inline-flex items-center gap-1.5" data-testid="ovr-extra-add-btn">
            <Plus size={14} /> {t("ovr.extraAdd")}
          </button>
        </div>
        <p className="text-eh-muted text-sm mb-4">{t("ovr.extraSubtitle")}</p>

        {extra.length === 0 && (
          <div className="text-eh-muted text-sm bg-white/50 border border-eh-border-soft rounded-2xl p-6 text-center" data-testid="ovr-extra-empty">
            {t("ovr.extraEmpty")}
          </div>
        )}

        <div className="space-y-2">
          {extra.map((item) => {
            const names = (item.service_ids || []).map(nameOf).filter(Boolean);
            return (
              <div key={item.id} className="flex items-center gap-3 bg-white/60 border border-eh-border-soft rounded-xl px-4 py-2.5" data-testid={`ovr-extra-item-${item.id}`}>
                <CalendarPlus size={16} className="text-eh-sage-deep shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-eh-ink">
                    {fmtDate(item.date, loc)} · {item.start}–{item.end}
                  </div>
                  <div className="text-xs text-eh-muted truncate" data-testid={`ovr-extra-services-${item.id}`}>
                    {names.length ? names.join(", ") : t("ovr.extraServicesUnknown")}
                  </div>
                  {item.reason && <div className="text-xs text-eh-muted truncate">{item.reason}</div>}
                </div>
                <button onClick={() => remove(item, "ovr.extraConfirmDelete", "ovr.extraDeleted")} className="p-2 rounded-full hover:bg-red-50 text-red-600 shrink-0" data-testid={`ovr-extra-delete-${item.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })}
        </div>
      </section>

      {closureDialogOpen && <OverrideDialog onClose={() => setClosureDialogOpen(false)} onSaved={onChanged} />}
      {extraDialogOpen && (
        <ExtraHoursDialog
          services={services.filter((s) => s.active)}
          onClose={() => setExtraDialogOpen(false)}
          onSaved={onChanged}
        />
      )}
    </div>
  );
};
